import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Sky } from "three/addons/objects/Sky.js";
import { Timer } from "three/addons/misc/Timer.js";
import GUI from "lil-gui";

/**
 * Base
 */
// Debug
const gui = new GUI();

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scence
const scence = new THREE.Scene();

/**
 * Textures
 */
const textureLoader = new THREE.TextureLoader();

//Floor
const floorAlphaTexture = textureLoader.load("./floor/alpha.webp");
const floorColorTexture = textureLoader.load(
  "./floor/forest_leaves_02_diffuse_1k.webp"
);
const floorARMTexture = textureLoader.load(
  "./floor/forest_leaves_02_arm_1k.webp"
);
const floorNormalTexture = textureLoader.load(
  "./floor/forest_leaves_02_nor_gl_1k.webp"
);
const floorDisplacementTexture = textureLoader.load(
  "./floor/forest_leaves_02_disp_1k.webp"
);

floorColorTexture.colorSpace = THREE.SRGBColorSpace;

floorColorTexture.repeat.set(9, 9);
floorARMTexture.repeat.set(9, 9);
floorNormalTexture.repeat.set(9, 9);
floorDisplacementTexture.repeat.set(9, 9);

floorColorTexture.wrapS = THREE.RepeatWrapping;
floorARMTexture.wrapS = THREE.RepeatWrapping;
floorNormalTexture.wrapS = THREE.RepeatWrapping;
floorDisplacementTexture.wrapS = THREE.RepeatWrapping;

floorColorTexture.wrapT = THREE.RepeatWrapping;
floorARMTexture.wrapT = THREE.RepeatWrapping;
floorNormalTexture.wrapT = THREE.RepeatWrapping;
floorDisplacementTexture.wrapT = THREE.RepeatWrapping;

// Wall
const wallColorTexture = textureLoader.load("./wall/rock_wall_08_diff_1k.jpg");
const wallARMTexture = textureLoader.load("./wall/rock_wall_08_arm_1k.jpg");
const wallNormalTexture = textureLoader.load(
  "./wall/rock_wall_08_nor_gl_1k.jpg"
);

wallColorTexture.colorSpace = THREE.SRGBColorSpace;

// Roof
const roofColorTexture = textureLoader.load("./roof/reed_roof_03_diff_1k.webp");
const roofARMTexture = textureLoader.load("./roof/reed_roof_03_arm_1k.webp");
const roofNormalTexture = textureLoader.load(
  "./roof/reed_roof_03_diff_1k.webp"
);

roofColorTexture.colorSpace = THREE.SRGBColorSpace;

roofColorTexture.repeat.set(3, 1);
roofARMTexture.repeat.set(3, 1);
roofNormalTexture.repeat.set(3, 1);

roofColorTexture.wrapS = THREE.RepeatWrapping;
roofARMTexture.wrapS = THREE.RepeatWrapping;
roofNormalTexture.wrapS = THREE.RepeatWrapping;

// Bush
const bushColorTexture = textureLoader.load(
  "./bush/forest_leaves_04_diff_1k.webp"
);
const bushARMTexture = textureLoader.load(
  "./bush/forest_leaves_04_arm_1k.webp"
);
const bushNormalTexture = textureLoader.load(
  "./bush/forest_leaves_04_nor_gl_1k.webp"
);

bushColorTexture.colorSpace = THREE.SRGBColorSpace;

bushARMTexture.repeat.set(2, 1);
bushColorTexture.repeat.set(2, 1);
bushNormalTexture.repeat.set(2, 1);

bushColorTexture.wrapS = THREE.RepeatWrapping;
bushARMTexture.wrapS = THREE.RepeatWrapping;
bushNormalTexture.wrapS = THREE.RepeatWrapping;

// Grave
const graveColorTexture = textureLoader.load(
  "./grave/white_plaster_rough_02_diff_1k.webp"
);
const graveARMTexture = textureLoader.load(
  "./grave/white_plaster_rough_02_arm_1k.webp"
);
const graveNormalTexture = textureLoader.load(
  "./grave/white_plaster_rough_02_nor_gl_1k.webp"
);

graveColorTexture.colorSpace = THREE.SRGBColorSpace;

graveARMTexture.repeat.set(0.3, 0.4);
graveColorTexture.repeat.set(0.3, 0.4);
graveNormalTexture.repeat.set(0.3, 0.4);

// Door
const doorColorTexture = textureLoader.load("./door/color.webp");
const doorAlphaTexture = textureLoader.load("./door/alpha.webp");
const doorAmbientOcclusionTexture = textureLoader.load(
  "./door/ambientOcclusion.webp"
);
const doorHeightTexture = textureLoader.load("./door/height.webp");
const doorNormalTexture = textureLoader.load("./door/normal.webp");
const doorMetalnessTexture = textureLoader.load("./door/metalness.webp");
const doorRoughnessTexture = textureLoader.load("./door/roughness.webp");

doorColorTexture.colorSpace = THREE.SRGBColorSpace;

/**
 * House
 */

// Floor
const floor = new THREE.Mesh(
  new THREE.PlaneGeometry(20, 20, 100, 100),
  new THREE.MeshStandardMaterial({
    transparent: true,
    alphaMap: floorAlphaTexture,
    map: floorColorTexture,
    aoMap: floorARMTexture,
    roughnessMap: floorARMTexture,
    metalnessMap: floorARMTexture,
    normalMap: floorNormalTexture,
    displacementMap: floorDisplacementTexture,
    displacementScale: 0.3,
    displacementBias: -0.1,
  })
);
floor.rotation.x = -Math.PI * 0.5;
scence.add(floor);

gui
  .add(floor.material, "displacementScale")
  .min(0)
  .max(1)
  .name("FloowDisplacementScale");
gui
  .add(floor.material, "displacementBias")
  .min(-1)
  .max(1)
  .name("FloowDisplacementBias");

// House Container
const house = new THREE.Group();
scence.add(house);

// Walls
const walls = new THREE.Mesh(
  new THREE.BoxGeometry(4, 2.5, 4),
  new THREE.MeshStandardMaterial({
    map: wallColorTexture,
    aoMap: wallARMTexture,
    roughnessMap: wallARMTexture,
    metalnessMap: wallARMTexture,
    normalMap: wallNormalTexture,
  })
);
walls.position.y = 1.25;
house.add(walls);

// Roof
const roof = new THREE.Mesh(
  new THREE.ConeGeometry(3.5, 1.5, 4),
  new THREE.MeshStandardMaterial({
    map: roofColorTexture,
    aoMap: roofARMTexture,
    roughnessMap: roofARMTexture,
    metalnessMap: roofARMTexture,
    normalMap: roofNormalTexture,
  })
);
roof.position.y = 2.5 + 0.75;
roof.rotation.y = Math.PI * 0.25;
house.add(roof);

// Chimney
const chimney = new THREE.Mesh(
  new THREE.BoxGeometry(0.4, 1.2, 0.4),
  new THREE.MeshStandardMaterial({
    map: wallColorTexture,
    aoMap: wallARMTexture,
    roughnessMap: wallARMTexture,
    metalnessMap: wallARMTexture,
    normalMap: wallNormalTexture,
  })
);
chimney.position.set(1.2, 3.8, 1.2);
house.add(chimney);

// Smoke particles
const smokeGeometry = new THREE.BufferGeometry();
const smokeCount = 200;
const smokePositions = new Float32Array(smokeCount * 3);
const smokeOpacities = new Float32Array(smokeCount);
const smokeSizes = new Float32Array(smokeCount);

for (let i = 0; i < smokeCount; i++) {
  smokePositions[i * 3] = (Math.random() - 0.5) * 0.3;
  smokePositions[i * 3 + 1] = Math.random() * 8;
  smokePositions[i * 3 + 2] = (Math.random() - 0.5) * 0.3;

  smokeOpacities[i] = Math.random();
  smokeSizes[i] = Math.random() * 0.3 + 0.1;
}

smokeGeometry.setAttribute(
  "position",
  new THREE.BufferAttribute(smokePositions, 3)
);
smokeGeometry.setAttribute(
  "opacity",
  new THREE.BufferAttribute(smokeOpacities, 1)
);
smokeGeometry.setAttribute("size", new THREE.BufferAttribute(smokeSizes, 1));

const smokeMaterial = new THREE.PointsMaterial({
  size: 0.3,
  sizeAttenuation: true,
  color: "#666666",
  transparent: true,
  opacity: 0.6,
  depthWrite: false,
  blending: THREE.AdditiveBlending,
});

const smoke = new THREE.Points(smokeGeometry, smokeMaterial);
smoke.position.set(1.2, 4.4, 1.2);
house.add(smoke);

// Door
const door = new THREE.Mesh(
  new THREE.PlaneGeometry(2.2, 2.2, 100, 100),
  new THREE.MeshStandardMaterial({
    color: "grey",
    map: doorColorTexture,
    transparent: true,
    alphaMap: doorAlphaTexture,
    aoMap: doorAmbientOcclusionTexture,
    displacementMap: doorHeightTexture,
    displacementScale: 0.15,
    displacementBias: -0.04,
    normalMap: doorNormalTexture,
    metalnessMap: doorMetalnessTexture,
    roughnessMap: doorRoughnessTexture,
  })
);
door.position.y = 1;
door.position.z = 2 + 0.01;
house.add(door);

// Windows
const windowGeometry = new THREE.PlaneGeometry(0.8, 1);
const windowMaterial = new THREE.MeshStandardMaterial({
  color: "#ffff88",
  transparent: true,
  opacity: 0.9,
  emissive: "#ffaa00",
  emissiveIntensity: 0.3,
});

// Front windows (next to door)
const window1 = new THREE.Mesh(windowGeometry, windowMaterial);
window1.position.set(-1, 1.2, 2.01);
house.add(window1);

const window2 = new THREE.Mesh(windowGeometry, windowMaterial);
window2.position.set(1, 1.2, 2.01);
house.add(window2);

// Side windows
const window3 = new THREE.Mesh(windowGeometry, windowMaterial);
window3.position.set(-2.01, 1.2, 0);
window3.rotation.y = Math.PI / 2;
house.add(window3);

const window4 = new THREE.Mesh(windowGeometry, windowMaterial);
window4.position.set(2.01, 1.2, 0);
window4.rotation.y = -Math.PI / 2;
house.add(window4);

// Window lights (inside the house)
const windowLight1 = new THREE.PointLight("#ffaa00", 2, 3);
windowLight1.position.set(-1, 1.2, 1.5);
house.add(windowLight1);

const windowLight2 = new THREE.PointLight("#ffaa00", 2, 3);
windowLight2.position.set(1, 1.2, 1.5);
house.add(windowLight2);

const windowLight3 = new THREE.PointLight("#ffaa00", 2, 3);
windowLight3.position.set(-1.5, 1.2, 0);
house.add(windowLight3);

const windowLight4 = new THREE.PointLight("#ffaa00", 2, 3);
windowLight4.position.set(1.5, 1.2, 0);
house.add(windowLight4);

// Bushes
const bushGeometry = new THREE.SphereGeometry(1, 16, 16);
const bushMaterial = new THREE.MeshStandardMaterial({
  color: "#ccffcc",
  map: bushColorTexture,
  aoMap: bushARMTexture,
  roughnessMap: bushARMTexture,
  metalnessMap: bushARMTexture,
  normalMap: bushNormalTexture,
});

const bush1 = new THREE.Mesh(bushGeometry, bushMaterial);
bush1.scale.setScalar(0.5);
bush1.position.set(0.8, 0.2, 2.2);
bush1.rotation.x = -0.75;

const bush2 = new THREE.Mesh(bushGeometry, bushMaterial);
bush2.scale.setScalar(0.25);
bush2.position.set(1.4, 0.1, 2.1);
bush2.rotation.x = -0.75;

const bush3 = new THREE.Mesh(bushGeometry, bushMaterial);
bush3.scale.setScalar(0.4);
bush3.position.set(-0.8, 0.1, 2.2);
bush3.rotation.x = -0.75;

const bush4 = new THREE.Mesh(bushGeometry, bushMaterial);
bush4.scale.setScalar(0.15);
bush4.position.set(-1, 0.05, 2.6);
bush4.rotation.x = -0.75;

house.add(bush1, bush2, bush3, bush4);

/**
 * Pumpkins
 */
const pumpkins = new THREE.Group();
scence.add(pumpkins);

const pumpkinGeometry = new THREE.SphereGeometry(0.4, 16, 12);
const pumpkinMaterial = new THREE.MeshStandardMaterial({
  color: "#ff6600",
  roughness: 0.8,
});

// Create jack-o'-lanterns around the scene
const pumpkinPositions = [
  { x: 2.5, z: 3, y: 0.4 },
  { x: -2.8, z: 2.5, y: 0.4 },
  { x: 4, z: -1, y: 0.4 },
  { x: -3.5, z: -2, y: 0.4 },
  { x: 1.5, z: -3.5, y: 0.4 },
  { x: -1.8, z: 4.2, y: 0.4 },
];

const pumpkinLights = [];

pumpkinPositions.forEach((pos, index) => {
  // Pumpkin body
  const pumpkin = new THREE.Mesh(pumpkinGeometry, pumpkinMaterial);
  pumpkin.position.set(pos.x, pos.y, pos.z);
  pumpkin.scale.y = 0.8; // Make it more pumpkin-shaped
  pumpkin.castShadow = true;
  pumpkin.receiveShadow = true;

  // Pumpkin stem
  const stemGeometry = new THREE.CylinderGeometry(0.05, 0.08, 0.2, 8);
  const stemMaterial = new THREE.MeshStandardMaterial({ color: "#4a5d23" });
  const stem = new THREE.Mesh(stemGeometry, stemMaterial);
  stem.position.set(pos.x, pos.y + 0.4, pos.z);
  stem.castShadow = true;

  // Jack-o'-lantern glow (from inside)
  const pumpkinLight = new THREE.PointLight("#ff4400", 3, 4);
  pumpkinLight.position.set(pos.x, pos.y, pos.z);
  pumpkinLights.push(pumpkinLight);

  // Face geometry (simple carved effect)
  const faceGeometry = new THREE.PlaneGeometry(0.6, 0.6);
  const faceMaterial = new THREE.MeshStandardMaterial({
    color: "#ff4400",
    transparent: true,
    opacity: 0.8,
    emissive: "#ff4400",
    emissiveIntensity: 0.5,
  });

  const face = new THREE.Mesh(faceGeometry, faceMaterial);
  face.position.set(pos.x, pos.y, pos.z + 0.35);

  pumpkins.add(pumpkin, stem, face, pumpkinLight);
});

// Graves
const graveGeometry = new THREE.BoxGeometry(0.6, 0.8, 0.2);
const graveMaterial = new THREE.MeshStandardMaterial({
  color: "gray",
  map: graveColorTexture,
  aoMap: graveARMTexture,
  roughnessMap: graveARMTexture,
  metalnessMap: graveARMTexture,
  normalMap: graveNormalTexture,
});

const graves = new THREE.Group();
scence.add(graves);

for (let i = 0; i < 60; i++) {
  const angle = Math.random() * Math.PI * 2;
  const radius = 3 + Math.random() * 4;
  const x = Math.sin(angle) * radius;
  const z = Math.cos(angle) * radius;

  // Mesh
  const grave = new THREE.Mesh(graveGeometry, graveMaterial);
  grave.position.x = x;
  grave.position.z = z;
  grave.position.y = Math.random() * 0.4;
  grave.rotation.x = (Math.random() - 0.5) * 0.4;
  grave.rotation.y = (Math.random() - 0.5) * 0.4;
  grave.rotation.z = (Math.random() - 0.5) * 0.4;

  // Add to graves group
  graves.add(grave);
}

/**
 * Trees
 */
const trees = new THREE.Group();
scence.add(trees);

// Tree materials
const trunkMaterial = new THREE.MeshStandardMaterial({
  color: "#3d2914",
  roughness: 0.8,
});
const branchMaterial = new THREE.MeshStandardMaterial({
  color: "#2d1a0a",
  roughness: 0.9,
});

// Create spooky trees
for (let i = 0; i < 12; i++) {
  const angle = Math.random() * Math.PI * 2;
  const radius = 8 + Math.random() * 6;
  const x = Math.sin(angle) * radius;
  const z = Math.cos(angle) * radius;

  // Tree trunk
  const trunkHeight = 3 + Math.random() * 2;
  const trunk = new THREE.Mesh(
    new THREE.CylinderGeometry(0.1, 0.2, trunkHeight, 8),
    trunkMaterial
  );
  trunk.position.set(x, trunkHeight / 2, z);
  trunk.castShadow = true;

  // Tree branches
  const branchCount = 3 + Math.floor(Math.random() * 4);
  for (let j = 0; j < branchCount; j++) {
    const branch = new THREE.Mesh(
      new THREE.ConeGeometry(0.05, 0.8 + Math.random() * 0.5, 6),
      branchMaterial
    );

    const branchAngle = (j / branchCount) * Math.PI * 2 + Math.random() * 0.5;
    const branchDistance = 0.3 + Math.random() * 0.2;
    branch.position.set(
      x + Math.cos(branchAngle) * branchDistance,
      trunkHeight * 0.6 + Math.random() * 0.5,
      z + Math.sin(branchAngle) * branchDistance
    );
    branch.rotation.z = (Math.random() - 0.5) * 0.8;
    branch.rotation.x = (Math.random() - 0.5) * 0.4;
    branch.castShadow = true;

    trees.add(branch);
  }

  trees.add(trunk);
}

/**
 * Fence
 */
const fence = new THREE.Group();
scence.add(fence);

const fencePostGeometry = new THREE.BoxGeometry(0.1, 1.5, 0.1);
const fenceRailGeometry = new THREE.BoxGeometry(1.2, 0.08, 0.08);
const fenceMaterial = new THREE.MeshStandardMaterial({
  color: "#4a3728",
  roughness: 0.9,
});

// Create fence around the house
const fenceRadius = 5.5;
const fenceSegments = 24;

for (let i = 0; i < fenceSegments; i++) {
  const angle = (i / fenceSegments) * Math.PI * 2;
  const x = Math.cos(angle) * fenceRadius;
  const z = Math.sin(angle) * fenceRadius;

  // Fence post
  const post = new THREE.Mesh(fencePostGeometry, fenceMaterial);
  post.position.set(x, 0.75, z);

  // Add some randomness to make it look old and broken
  post.rotation.z = (Math.random() - 0.5) * 0.2;
  post.scale.y = 0.7 + Math.random() * 0.6; // Some posts are broken
  post.castShadow = true;

  fence.add(post);

  // Fence rail (connect to next post)
  if (i < fenceSegments - 1) {
    const nextAngle = ((i + 1) / fenceSegments) * Math.PI * 2;
    const nextX = Math.cos(nextAngle) * fenceRadius;
    const nextZ = Math.sin(nextAngle) * fenceRadius;

    const rail = new THREE.Mesh(fenceRailGeometry, fenceMaterial);
    rail.position.set((x + nextX) / 2, 0.8, (z + nextZ) / 2);
    rail.lookAt(nextX, 0.8, nextZ);
    rail.rotation.y += Math.PI / 2;

    // Some rails are missing or broken
    if (Math.random() > 0.15) {
      rail.scale.x = 0.6 + Math.random() * 0.4;
      rail.castShadow = true;
      fence.add(rail);
    }
  }
}

/**
 * Lights
 */
// Ambient light
const ambientLight = new THREE.AmbientLight("#86cdff", 0.275);
scence.add(ambientLight);

// Directional Light
const directionalLight = new THREE.DirectionalLight("#86cdff", 1);
directionalLight.position.set(3, 2, -8);
scence.add(directionalLight);

// Door Light
const doorLight = new THREE.PointLight("#ff7d46", 5);
doorLight.position.set(0, 2.012, 2.5);
house.add(doorLight);

/**
 * Ghosts
 */
const ghost1 = new THREE.PointLight("#9900ff", 6);
const ghost2 = new THREE.PointLight("#00ff99", 6);
const ghost3 = new THREE.PointLight("#ff0000", 6);
scence.add(ghost1, ghost2, ghost3);

/**
 * Bats
 */
const bats = new THREE.Group();
scence.add(bats);

const batGeometry = new THREE.ConeGeometry(0.1, 0.3, 3);
const batMaterial = new THREE.MeshStandardMaterial({
  color: "#000000",
  roughness: 0.8,
});

const batData = [];

for (let i = 0; i < 8; i++) {
  const bat = new THREE.Mesh(batGeometry, batMaterial);
  bat.rotation.x = Math.PI / 2;
  bat.castShadow = true;

  // Store bat animation data
  batData.push({
    mesh: bat,
    angle: Math.random() * Math.PI * 2,
    radius: 4 + Math.random() * 8,
    speed: 0.5 + Math.random() * 0.8,
    height: 2 + Math.random() * 4,
    heightOffset: Math.random() * Math.PI * 2,
  });

  bats.add(bat);
}

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.addEventListener("resize", () => {
  // update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  0.1,
  100
);
camera.position.x = 2.7;
camera.position.y = 2;
camera.position.z = 9;
scence.add(camera);

/**
 * Controls
 */
const control = new OrbitControls(camera, canvas);
control.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  antialias: true,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio), 2);

// Enhanced renderer settings for better visuals
renderer.toneMapping = THREE.ReinhardToneMapping;
renderer.toneMappingExposure = 0.8;
renderer.outputColorSpace = THREE.SRGBColorSpace;

/**
 * Shadows
 */
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;

//Cast and receive
directionalLight.castShadow = true;
ghost1.castShadow = true;
ghost2.castShadow = true;
ghost3.castShadow = true;

walls.castShadow = true;
walls.receiveShadow = true;
roof.castShadow = true;
floor.receiveShadow = true;

for (const grave of graves.children) {
  grave.castShadow = true;
  grave.receiveShadow = true;
}

// Mapping
directionalLight.shadow.mapSize.width = 256;
directionalLight.shadow.mapSize.height = 256;
directionalLight.shadow.camera.top = 8;
directionalLight.shadow.camera.right = 8;
directionalLight.shadow.camera.bottom = -8;
directionalLight.shadow.camera.left = -8;
directionalLight.shadow.camera.near = 1;
directionalLight.shadow.camera.far = 20;

ghost1.shadow.mapSize.width = 256;
ghost1.shadow.mapSize.height = 256;
ghost1.shadow.camera.far = 10;

ghost2.shadow.mapSize.width = 256;
ghost2.shadow.mapSize.height = 256;
ghost2.shadow.camera.far = 10;

ghost3.shadow.mapSize.width = 256;
ghost3.shadow.mapSize.height = 256;
ghost3.shadow.camera.far = 10;

/**
 * Sky
 */
const sky = new Sky();
sky.material.uniforms["turbidity"].value = 10;
sky.material.uniforms["rayleigh"].value = 3;
sky.material.uniforms["mieCoefficient"].value = 0.1;
sky.material.uniforms["mieDirectionalG"].value = 0.95;
sky.material.uniforms["sunPosition"].value.set(0.3, -0.038, -0.95);
sky.scale.set(100, 100, 100);
scence.add(sky);

/**
 * Fog
 */
scence.fog = new THREE.FogExp2("#02343f", 0.08);

// Add atmospheric particles
const atmosphereGeometry = new THREE.BufferGeometry();
const atmosphereCount = 500;
const atmospherePositions = new Float32Array(atmosphereCount * 3);

for (let i = 0; i < atmosphereCount; i++) {
  atmospherePositions[i * 3] = (Math.random() - 0.5) * 40;
  atmospherePositions[i * 3 + 1] = Math.random() * 20;
  atmospherePositions[i * 3 + 2] = (Math.random() - 0.5) * 40;
}

atmosphereGeometry.setAttribute(
  "position",
  new THREE.BufferAttribute(atmospherePositions, 3)
);

const atmosphereMaterial = new THREE.PointsMaterial({
  size: 0.03,
  sizeAttenuation: true,
  color: "#86cdff",
  transparent: true,
  opacity: 0.3,
  depthWrite: false,
});

const atmosphere = new THREE.Points(atmosphereGeometry, atmosphereMaterial);
scence.add(atmosphere);

/**
 * Animate
 */
const timer = new Timer();

/**
 * Audio
 */
let audioContext;
let windSound;
let isAudioEnabled = false;

// Create atmospheric wind sound using Web Audio API
function createWindSound() {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }

  // Create wind sound using noise and filters
  const bufferSize = audioContext.sampleRate * 2;
  const buffer = audioContext.createBuffer(
    1,
    bufferSize,
    audioContext.sampleRate
  );
  const data = buffer.getChannelData(0);

  // Generate brown noise for wind effect
  let lastOut = 0;
  for (let i = 0; i < bufferSize; i++) {
    const white = Math.random() * 2 - 1;
    data[i] = (lastOut + 0.02 * white) / 1.02;
    lastOut = data[i];
    data[i] *= 0.1; // Reduce volume
  }

  const source = audioContext.createBufferSource();
  source.buffer = buffer;
  source.loop = true;

  // Apply low-pass filter for wind effect
  const filter = audioContext.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 300;
  filter.Q.value = 1;

  const gainNode = audioContext.createGain();
  gainNode.gain.value = 0.15;

  source.connect(filter);
  filter.connect(gainNode);
  gainNode.connect(audioContext.destination);

  return { source, gainNode };
}

// Enable audio on first user interaction
document.addEventListener(
  "click",
  () => {
    if (!isAudioEnabled) {
      windSound = createWindSound();
      windSound.source.start();
      isAudioEnabled = true;
    }
  },
  { once: true }
);

const tick = () => {
  //Timer
  timer.update();
  const elapsedTime = timer.getElapsed();

  // Ghost
  const ghost1Angle = elapsedTime * 0.6;
  ghost1.position.x = Math.cos(ghost1Angle) * 6;
  ghost1.position.z = Math.sin(ghost1Angle) * 6;
  ghost1.position.y =
    Math.sin(ghost1Angle) *
    Math.sin(ghost1Angle * 2.34) *
    Math.sin(ghost1Angle * 3.45);

  const ghost2Angle = -elapsedTime * 0.45;
  ghost2.position.x = Math.cos(ghost2Angle) * 3;
  ghost2.position.z = Math.sin(ghost2Angle) * 6;
  ghost2.position.y =
    Math.sin(ghost2Angle) *
    Math.sin(ghost2Angle * 2.34) *
    Math.sin(ghost2Angle * 3.45);

  const ghost3Angle = -elapsedTime * 0.54;
  ghost3.position.x = Math.cos(ghost3Angle) * 6;
  ghost3.position.z = Math.sin(ghost3Angle) * 9;
  ghost3.position.y =
    Math.sin(ghost3Angle) *
    Math.sin(ghost3Angle * 2.34) *
    Math.sin(ghost3Angle * 3.45);

  // Animate bats
  batData.forEach((batInfo, index) => {
    batInfo.angle += batInfo.speed * 0.01;

    batInfo.mesh.position.x = Math.cos(batInfo.angle) * batInfo.radius;
    batInfo.mesh.position.z = Math.sin(batInfo.angle) * batInfo.radius;
    batInfo.mesh.position.y =
      batInfo.height + Math.sin(elapsedTime * 2 + batInfo.heightOffset) * 0.5;

    // Make bats face their direction of movement
    batInfo.mesh.rotation.y = batInfo.angle + Math.PI / 2;

    // Add some wing flapping effect
    batInfo.mesh.rotation.z = Math.sin(elapsedTime * 8 + index) * 0.3;
  });

  // Animate smoke
  const smokePositions = smoke.geometry.attributes.position.array;
  for (let i = 0; i < smokeCount; i++) {
    const i3 = i * 3;

    // Move smoke upward
    smokePositions[i3 + 1] += 0.02;

    // Add some horizontal drift
    smokePositions[i3] += Math.sin(elapsedTime + i) * 0.001;
    smokePositions[i3 + 2] += Math.cos(elapsedTime + i) * 0.001;

    // Reset particles that go too high
    if (smokePositions[i3 + 1] > 8) {
      smokePositions[i3] = (Math.random() - 0.5) * 0.3;
      smokePositions[i3 + 1] = 0;
      smokePositions[i3 + 2] = (Math.random() - 0.5) * 0.3;
    }
  }
  smoke.geometry.attributes.position.needsUpdate = true;

  // Animate window lights (flickering effect)
  const flickerIntensity =
    2 + Math.sin(elapsedTime * 15) * 0.3 + Math.sin(elapsedTime * 8.5) * 0.2;
  windowLight1.intensity = flickerIntensity;
  windowLight2.intensity = flickerIntensity * 0.9;
  windowLight3.intensity = flickerIntensity * 1.1;
  windowLight4.intensity = flickerIntensity * 0.8;

  // Animate pumpkin lights (spooky flickering)
  pumpkinLights.forEach((light, index) => {
    const pumpkinFlicker =
      3 +
      Math.sin(elapsedTime * 12 + index * 2) * 0.5 +
      Math.sin(elapsedTime * 6.5 + index) * 0.3;
    light.intensity = pumpkinFlicker;
  });

  // Dynamic audio effects
  if (windSound && windSound.gainNode) {
    // Vary wind intensity based on time
    const windIntensity = 0.1 + Math.sin(elapsedTime * 0.5) * 0.05;
    windSound.gainNode.gain.value = windIntensity;
  }

  // Animate atmospheric particles
  atmosphere.rotation.y = elapsedTime * 0.02;
  atmosphere.position.y = Math.sin(elapsedTime * 0.5) * 0.2;

  // Update Controls
  control.update();

  // Render
  renderer.render(scence, camera);

  // Call tick again ont he next frame
  window.requestAnimationFrame(tick);
};

tick();
